import { useSidecarStore } from './useSidecarStore';
import {
  isDocumentPictureInPictureSupported,
  isPipDocumentFullscreen,
} from './sidecarBehavior';

export const SIDECAR_PIP_WIDTH = 760;
export const SIDECAR_PIP_HEIGHT = 640;

type DocumentPictureInPicture = {
  requestWindow: (options?: {
    width?: number;
    height?: number;
    disallowReturnToOpener?: boolean;
    preferInitialWindowPlacement?: boolean;
  }) => Promise<Window>;
};

let currentPipWindow: Window | null = null;

export function getSidecarPipWindow(): Window | null {
  if (currentPipWindow?.closed) currentPipWindow = null;
  return currentPipWindow;
}

export function copyDocumentStyles(from: Document, to: Document): void {
  if ('adoptedStyleSheets' in from && 'adoptedStyleSheets' in to) {
    try {
      to.adoptedStyleSheets = [...from.adoptedStyleSheets];
    } catch {
      // Some browsers refuse to share constructed sheets across documents.
    }
  }

  for (const sheet of Array.from(from.styleSheets)) {
    try {
      const cssRules = Array.from(sheet.cssRules)
        .map((rule) => rule.cssText)
        .join('');
      const style = to.createElement('style');
      style.textContent = cssRules;
      to.head.appendChild(style);
    } catch {
      if (sheet.href) {
        const link = to.createElement('link');
        link.rel = 'stylesheet';
        link.href = sheet.href;
        to.head.appendChild(link);
      }
    }
  }
}

export function preparePipDocument(target: Document): void {
  target.documentElement.className = document.documentElement.className;
  target.documentElement.style.height = '100%';
  target.body.style.margin = '0';
  target.body.style.height = '100%';
  target.body.style.overflow = 'hidden';
  target.body.style.background = '#ffffff';
  target.body.style.display = 'flex';
  target.body.style.flexDirection = 'column';
}

export function closeSidecarPipWindow(): void {
  const pip = currentPipWindow;
  currentPipWindow = null;
  if (pip && !pip.closed) pip.close();
}

export function isSidecarPipFullscreen(): boolean {
  const pip = getSidecarPipWindow();
  return isPipDocumentFullscreen(pip?.document ?? null);
}

/**
 * Plein écran de la fenêtre Chrome Picture-in-Picture (son document),
 * pas du panneau React. `resizeTo` est ignoré par Chrome sur ce type de fenêtre.
 */
export async function toggleSidecarPipFullscreen(): Promise<boolean> {
  const pip = getSidecarPipWindow();
  if (!pip) return false;
  const doc = pip.document;
  try {
    if (doc.fullscreenElement) {
      await doc.exitFullscreen();
      return false;
    }
    await doc.documentElement.requestFullscreen();
    return true;
  } catch {
    return isPipDocumentFullscreen(doc);
  }
}

export async function openSidecarPipWindow(): Promise<Window | null> {
  const win = window as Window & { documentPictureInPicture?: DocumentPictureInPicture };
  if (!isDocumentPictureInPictureSupported(window) || !win.documentPictureInPicture) {
    return null;
  }

  const existing = getSidecarPipWindow();
  if (existing) {
    useSidecarStore.getState().enterPip();
    return existing;
  }

  // Mark PiP active before awaiting requestWindow so a click-outside
  // (Chrome steals focus when the window opens) cannot close() the sidecar.
  useSidecarStore.getState().enterPip();

  try {
    const pip = await win.documentPictureInPicture.requestWindow({
      width: SIDECAR_PIP_WIDTH,
      height: SIDECAR_PIP_HEIGHT,
    });

    currentPipWindow = pip;
    copyDocumentStyles(document, pip.document);
    preparePipDocument(pip.document);

    pip.addEventListener('pagehide', () => {
      currentPipWindow = null;
      useSidecarStore.getState().exitPip();
    });

    return pip;
  } catch (err) {
    useSidecarStore.getState().exitPip();
    throw err;
  }
}
